/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useId, useRef, useState } from "react";

import { ValueSetExpansionContains } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import {
  Badge,
  Combobox,
  Group,
  Pill,
  PillsInput,
  Stack,
  TextInput,
  useCombobox,
} from "@mantine/core";
import { UseFormReturnType } from "@mantine/form";
import { useDebouncedValue } from "@mantine/hooks";

import { IconSearch } from "@tabler/icons-react";

import { searchCombinedProcedureCodes } from "@/settings";
import { CUSTOM_PROCEDURE_OPTION_DISPLAY_SUFFIX } from "@/shared/constants/custom-procedure-option-display";
import { parseCustomProcedureOptionDisplay } from "@/shared/utils/custom-procedure-option-display";
import { getValueSetOptions } from "@/utils/medplum";
import { hideToast, toast } from "@/utils/notifications";
import { captureExceptionWithoutPhi } from "@/utils/sentry";

import { TextWrapper } from "../TextWrapper";

import { canOpenDropdown } from "./utils/can-open-dropdown";

/** Joins FHIR Coding `system` and `code` for stable Combobox identity when the same code appears under multiple systems. */
const PROCEDURE_OPTION_KEY_SEPARATOR = "\u001f";
const COMBINED_PROCEDURE_CODES_URL_SEGMENT = "/combined-procedure-codes/";
const SEARCH_RESULT_LIMIT = 10;
const CODE_SEARCH_ERROR_TOAST_ID_PREFIX = "coding-input-search-error";
/** Extra results fetched when a filter is set, so filtered-out codes do not take result slots. */
const FILTERED_SEARCH_EXTRA_RESULTS = 10;

function procedureOptionCompositeKey(system: string | undefined, code: string | undefined): string {
  return `${system ?? ""}${PROCEDURE_OPTION_KEY_SEPARATOR}${code ?? ""}`;
}

function findCodingByCompositeKey(
  rows: ValueSetExpansionContains[] | undefined,
  compositeKey: string,
): ValueSetExpansionContains | undefined {
  return rows?.find((item) => procedureOptionCompositeKey(item.system, item.code) === compositeKey);
}

type CodingInputWithDetailsProps<T> = {
  valueSetUrl: string;
  form: UseFormReturnType<T>;
  label?: string;
  name: string;
  multiple?: boolean;
  placeholder?: string;
  /** Read-only: the current selection stays visible but cannot be searched, added to, or removed. */
  disabled?: boolean;
  /**
   * Multiple mode only: shapes the form entry for a picked coding. Without it the bare coding is
   * stored, so a field whose entries carry more than a coding has to pass its own builder.
   */
  buildEntry?: (coding: ValueSetExpansionContains) => unknown;
  /** Keeps only the search results it returns true for. */
  filterOption?: (coding: ValueSetExpansionContains) => boolean;
  /**
   * Shown when the search found codes but filterOption hid all of them, so a valid code is not
   * reported as wrong. Without it the usual "No results found" state shows.
   */
  filteredOutMessage?: string;
  "data-testid"?: string;
};

type ValueSetOption = {
  value: string;
  label: string;
  display: string;
};

function renderDisplayWithCustomBadge(display: string): JSX.Element {
  const { baseDisplay, hasCustomSuffix } = parseCustomProcedureOptionDisplay(display);
  return (
    <Group gap="xs" wrap="wrap" align="center">
      <span>{baseDisplay}</span>
      {hasCustomSuffix ? (
        <Badge
          size="xs"
          radius="sm"
          color="blue.6"
          variant="outline"
          styles={{
            root: { borderWidth: 1.5 },
            label: { color: "var(--mantine-color-dark-9)" },
          }}
        >
          {CUSTOM_PROCEDURE_OPTION_DISPLAY_SUFFIX}
        </Badge>
      ) : null}
    </Group>
  );
}

const CodingInputWithDetails = <T,>({
  form,
  label,
  valueSetUrl,
  name,
  multiple = false,
  placeholder,
  disabled = false,
  buildEntry,
  filterOption,
  filteredOutMessage,
  "data-testid": dataTestId,
}: CodingInputWithDetailsProps<T>) => {
  const medplum = useMedplum();
  const [search, setSearch] = useState("");
  const [fetchedCodings, setFetchedCodings] = useState<ValueSetExpansionContains[]>();
  // Filtered on each render, so a filter that changes (e.g. a new service date) applies to the
  // results already shown without searching again.
  const codingValue = filterOption
    ? fetchedCodings?.filter(filterOption).slice(0, SEARCH_RESULT_LIMIT)
    : fetchedCodings;
  const isEverythingFilteredOut =
    (fetchedCodings?.length ?? 0) > 0 && (codingValue?.length ?? 0) === 0;
  const valueSetParsedOptions: ValueSetOption[] = (codingValue ?? []).map((item) => ({
    value: procedureOptionCompositeKey(item.system, item.code),
    label: item.code ?? "",
    display: item.display ?? "",
  }));

  const fieldValue = form.getInputProps(name)?.value;
  const fieldError = form.getInputProps(name)?.error;
  const selectedFormCoding = !multiple
    ? (fieldValue as ValueSetExpansionContains | undefined)
    : undefined;
  const selectedFormCodings: ValueSetExpansionContains[] = multiple
    ? ((fieldValue as ValueSetExpansionContains[] | undefined) ?? [])
    : [];

  const selectedOptionDisplay = selectedFormCoding?.display ?? null;
  const hasSelectedCoding =
    !multiple && selectedFormCoding?.code !== undefined && selectedFormCoding.code !== "";

  const debouncedSearch = useDebouncedValue(search, 300)[0];
  // Each input owns its own error toast, so one input's successful search never hides another's.
  const searchErrorToastId = `${CODE_SEARCH_ERROR_TOAST_ID_PREFIX}-${useId()}`;
  const isCombinedProcedureCodesUrl = valueSetUrl.includes(COMBINED_PROCEDURE_CODES_URL_SEGMENT);
  const defersDropdownUntilSearch = isCombinedProcedureCodesUrl;

  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  // useCombobox returns a fresh object on every render, so it can never be a
  // dependency of the fetch effect below — the effect would re-run on every
  // render and its setState calls would loop forever. Read it through a ref
  // instead: the effect only needs to open the dropdown, not react to it.
  const comboboxRef = useRef(combobox);
  useEffect(() => {
    comboboxRef.current = combobox;
  });

  // Whether a filter is set changes how many results the search asks for; the filter itself is
  // applied on render, so a caller need not memoize it.
  const hasFilterOption = filterOption !== undefined;

  const openDropdown = () => {
    const canOpen = canOpenDropdown({
      defersDropdownUntilSearch,
      optionCount: valueSetParsedOptions.length,
      isEverythingFilteredOut,
      hasFilteredOutMessage: Boolean(filteredOutMessage),
    });
    if (!disabled && canOpen) combobox.openDropdown();
  };

  // An input's error toast closes with the input, e.g. when the drawer holding it closes.
  useEffect(() => () => hideToast(searchErrorToastId), [searchErrorToastId]);

  useEffect(() => {
    // A search that a newer one has replaced may still resolve last; its results are dropped.
    let isCurrentSearch = true;
    const fetchValueSet = async () => {
      if (defersDropdownUntilSearch && !debouncedSearch.trim()) {
        setFetchedCodings([]);
        return;
      }

      const searchParams = {
        filter: debouncedSearch,
        count: hasFilterOption
          ? SEARCH_RESULT_LIMIT + FILTERED_SEARCH_EXTRA_RESULTS
          : SEARCH_RESULT_LIMIT,
      };
      let fetchedResults: ValueSetExpansionContains[];
      try {
        // Procedure code searches add live CPT results to the practice's stored codes.
        fetchedResults = isCombinedProcedureCodesUrl
          ? await searchCombinedProcedureCodes(medplum, valueSetUrl, searchParams)
          : await getValueSetOptions(medplum, valueSetUrl, searchParams);
      } catch (error) {
        if (!isCurrentSearch) return;
        // Results from an earlier search no longer match the query, so they are cleared.
        setFetchedCodings([]);
        captureExceptionWithoutPhi("Code search failed", error, {
          feature: "coding-input-search",
          valueSetUrl,
        });
        toast({
          id: searchErrorToastId,
          title: "Couldn't search codes",
          message: "The code list didn't load. Try searching again.",
        });
        return;
      }
      if (!isCurrentSearch) return;
      // A search that succeeds clears the toast an earlier failed search left on screen.
      hideToast(searchErrorToastId);
      setFetchedCodings(fetchedResults);
      if (defersDropdownUntilSearch && fetchedResults.length > 0) {
        comboboxRef.current.openDropdown();
      }
    };
    fetchValueSet();
    return () => {
      isCurrentSearch = false;
    };
  }, [
    medplum,
    valueSetUrl,
    debouncedSearch,
    isCombinedProcedureCodesUrl,
    defersDropdownUntilSearch,
    hasFilterOption,
    searchErrorToastId,
  ]);

  const formSetFieldValue = form.setFieldValue;

  const onDeleteSelectedOption = () => {
    formSetFieldValue(name, undefined as any);
  };

  const onDeleteSelectedOptionByKey = (compositeKey: string) => {
    const newValues = selectedFormCodings.filter(
      (item) => procedureOptionCompositeKey(item.system, item.code) !== compositeKey,
    );
    formSetFieldValue(name, newValues as any);
  };

  const onOptionSubmit = (compositeKey: string) => {
    try {
      const selectedCoding = findCodingByCompositeKey(codingValue, compositeKey);

      if (!selectedCoding) {
        if (!multiple) formSetFieldValue(name, undefined as any);
        return;
      }

      if (!multiple) {
        formSetFieldValue(name, selectedCoding as any);
        return;
      }

      const alreadySelected = selectedFormCodings.some(
        (item) => procedureOptionCompositeKey(item.system, item.code) === compositeKey,
      );
      if (!alreadySelected) {
        const entry = buildEntry ? buildEntry(selectedCoding) : selectedCoding;
        formSetFieldValue(name, [...selectedFormCodings, entry] as any);
      }
    } finally {
      setSearch("");
      combobox.closeDropdown();
    }
  };

  const dropdownContent = (
    <Combobox.Dropdown mah={400} style={{ overflowY: "auto" }}>
      <Combobox.Options>
        {valueSetParsedOptions.length > 0 ? (
          valueSetParsedOptions.map((option) => (
            <Combobox.Option key={option.value} value={option.value}>
              <Stack gap="xs">
                <TextWrapper fw="bold" size="lg" c="black">
                  {option.label}
                </TextWrapper>
                {renderDisplayWithCustomBadge(option.display)}
              </Stack>
            </Combobox.Option>
          ))
        ) : (
          <Combobox.Empty style={{ textAlign: "start" }}>
            {isEverythingFilteredOut && filteredOutMessage ? (
              <Stack gap="xs" data-testid="coding-input-filtered-out">
                <TextWrapper c="dark.9" variant="bold">
                  No available codes
                </TextWrapper>
                <TextWrapper c="gray.7">{filteredOutMessage}</TextWrapper>
              </Stack>
            ) : (
              <Stack gap="xs">
                <TextWrapper c="red.7" variant="bold">
                  No results found
                </TextWrapper>
                <TextWrapper c="black">Please check the code</TextWrapper>
              </Stack>
            )}
          </Combobox.Empty>
        )}
      </Combobox.Options>
    </Combobox.Dropdown>
  );

  if (multiple) {
    return (
      <Combobox shadow="sm" store={combobox} onOptionSubmit={onOptionSubmit}>
        <Combobox.Target>
          <PillsInput
            label={label}
            error={fieldError}
            disabled={disabled}
            onClick={openDropdown}
            data-testid={dataTestId}
            rightSection={<IconSearch size={18} />}
          >
            <Pill.Group>
              {selectedFormCodings.map((coding) => {
                const compositeKey = procedureOptionCompositeKey(coding.system, coding.code);
                const { baseDisplay } = parseCustomProcedureOptionDisplay(coding.display ?? "");
                return (
                  <Pill
                    key={compositeKey}
                    disabled={disabled}
                    withRemoveButton={!disabled}
                    onRemove={() => onDeleteSelectedOptionByKey(compositeKey)}
                    removeButtonProps={{ "aria-label": `Remove ${coding.code}` }}
                  >
                    {coding.code} - {baseDisplay}
                  </Pill>
                );
              })}
              <PillsInput.Field
                value={search}
                disabled={disabled}
                onFocus={openDropdown}
                placeholder={selectedFormCodings.length === 0 ? placeholder : undefined}
                onChange={(event) => {
                  openDropdown();
                  combobox.updateSelectedOptionIndex();
                  setSearch(event.currentTarget.value);
                }}
              />
            </Pill.Group>
          </PillsInput>
        </Combobox.Target>
        {dropdownContent}
      </Combobox>
    );
  }

  if (hasSelectedCoding) {
    return (
      <PillsInput
        disabled={disabled}
        data-testid={dataTestId}
        rightSection={<IconSearch size={18} />}
        {...form.getInputProps(name)}
      >
        <Pill
          disabled={disabled}
          withRemoveButton={!disabled}
          onRemove={onDeleteSelectedOption}
          removeButtonProps={{ "aria-label": "Clear search" }}
        >
          <Group gap="xs" wrap="wrap" align="center">
            <span>{selectedFormCoding.code} -</span>
            {renderDisplayWithCustomBadge(selectedOptionDisplay ?? "")}
          </Group>
        </Pill>
      </PillsInput>
    );
  }

  return (
    <Combobox shadow="sm" store={combobox} onOptionSubmit={onOptionSubmit}>
      <Combobox.Target>
        <TextInput
          data-testid={dataTestId}
          rightSection={<IconSearch size={18} />}
          {...form.getInputProps(name)}
          label={label}
          value={search}
          disabled={disabled}
          onClick={openDropdown}
          onFocus={openDropdown}
          placeholder={placeholder}
          rightSectionPointerEvents="none"
          onChange={(event) => {
            openDropdown();
            combobox.updateSelectedOptionIndex();
            setSearch(event.currentTarget.value);
          }}
        />
      </Combobox.Target>
      {dropdownContent}
    </Combobox>
  );
};

export default CodingInputWithDetails;
